import { OfferOrderRefundFailureReasonDto } from 'src/dtos';
import { OfferOrderRefund, OfferOrderStatus } from 'src/models';

import { ClientServicesLink } from '../ClientServicesLink/ClientServicesLink';
import { MTypography } from '../MTypography/MTypography';

interface OfferOrderRefundDisclaimerProps {
  value: OfferOrderRefund;
  status: OfferOrderStatus;
}

export const OfferOrderRefundDisclaimer = ({ value, status }: OfferOrderRefundDisclaimerProps) => {
  const renderIfYouHaveQuestionsDisclaimer = () => {
    if (status.isCancelled) {
      return (
        <>
          If you have any questions on why your order was cancelled please contact My IPO at{' '}
          <a href='mailto:clientservices@myipo.com'>clientservices@myipo.com</a>.
        </>
      );
    }

    return null;
  };

  const renderFailedRefundDisclaimer = () => {
    if (value.failureReason === OfferOrderRefundFailureReasonDto.LostOrStolenCard) {
      return (
        <MTypography variant='body1'>
          Your credit card refund payment failed because it was reported as lost/stolen. Please contact your credit card
          company and/or My IPO at <ClientServicesLink />.
        </MTypography>
      );
    }

    if (value.failureReason === OfferOrderRefundFailureReasonDto.ExpiredOrCanceledCard) {
      return (
        <MTypography variant='body1'>
          Your credit card refund payment failed because it was reported as an expired or canceled card. Please contact
          your credit card company and/or My IPO at <ClientServicesLink />.
        </MTypography>
      );
    }

    if (value.failureReason === OfferOrderRefundFailureReasonDto.ChargeForPendingRefundDisputed) {
      return (
        <MTypography variant='body1'>
          Your credit card refund payment failed because you disputed the charge/payment while the refund was. Please
          contact your credit card company and/or My IPO at <ClientServicesLink />.
        </MTypography>
      );
    }

    if (value.failureReason === OfferOrderRefundFailureReasonDto.InsufficientFunds) {
      return (
        <MTypography variant='body1'>
          Your refund is pending. Please contact My IPO at <ClientServicesLink /> with any questions.
        </MTypography>
      );
    }

    if (value.failureReason === OfferOrderRefundFailureReasonDto.Declined) {
      return (
        <MTypography variant='body1'>
          Your credit card refund was declined. Please contact your credit card company and/or My IPO at{' '}
          <ClientServicesLink />.
        </MTypography>
      );
    }

    if (value.failureReason === OfferOrderRefundFailureReasonDto.MerchantRequest) {
      return (
        <MTypography variant='body1'>
          Your credit card refund was declined. Please contact your credit card company and/or My IPO at{' '}
          <ClientServicesLink />.
        </MTypography>
      );
    }

    return (
      <MTypography variant='body1'>
        Your credit card refund failed for unknown reasons. Please contact your credit card company and/or My IPO at{' '}
        <ClientServicesLink />.
      </MTypography>
    );
  };

  const renderCardRefundDisclaimerByStatus = () => {
    if (value.status.isPending) {
      return (
        <MTypography variant='body1'>
          Your refund is pending. Once the refund has been successfully issued, it may take 5-10 business days for the
          funds to appear on your credit card account statement.
        </MTypography>
      );
    }

    if (value.status.hasSucceeded) {
      return (
        <MTypography variant='body1'>
          A refund has been successfully issued. It may take 5-10 business days for the funds to appear on your credit
          card account statement. {renderIfYouHaveQuestionsDisclaimer()}
        </MTypography>
      );
    }

    if (value.status.hasFailed) {
      return renderFailedRefundDisclaimer();
    }

    return null;
  };

  if (value.mechanism.isAch) {
    return (
      <MTypography variant='body1'>
        A refund using ACH has been successfully issued. It may take 5-10 business days for the funds to appear in your
        bank account statement. {renderIfYouHaveQuestionsDisclaimer()}
      </MTypography>
    );
  }

  if (value.mechanism.isWire) {
    return (
      <MTypography variant='body1'>
        A refund using Wire transfer has been successfully issued. It may take 1-3 business days for the funds to appear
        in your bank account statement. {renderIfYouHaveQuestionsDisclaimer()}
      </MTypography>
    );
  }

  if (value.mechanism.isCheck) {
    return (
      <MTypography variant='body1'>
        A refund using Check transfer has been successfully issued. It may take 7-14 business days for the check to
        arrive. {renderIfYouHaveQuestionsDisclaimer()}
      </MTypography>
    );
  }

  return renderCardRefundDisclaimerByStatus();
};
